import type { LiveVehicleState } from '@fleetcore/shared/telemetry'

import type { ChannelKey } from './api-types'

/**
 * Doors, seatbelts and lights as NAMED indicators — never as a hex number.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHY THESE IDS AND NOT AVL 90.
 * ─────────────────────────────────────────────────────────────────────────────
 * AVL id 90 "Door Status" is a two-byte word and no source behind this platform
 * states which bit is which door — `DOOR_STATUS_BITMASK_LEGEND_VERIFIED` in the shared
 * registry is `false` and is meant to stay false until somebody bench-tests it.
 * Publishing an invented legend would draw "porte arrière droite ouverte" from a bit
 * that means something else entirely: a specific claim, about a specific vehicle,
 * made up, and indistinguishable from a true one.
 *
 * The named booleans below come from ids the vendor names individually (654–658, 909,
 * 913 for the openings; 928–935 and 912 for the lights; 940–944 for the belts). Those
 * are transcriptions, not guesses. The opaque `door_status` bitmask keeps its own
 * bits-and-hex rendering elsewhere in the panel — it is still evidence, it is simply
 * not a door name.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  NULL IS NOT FALSE.
 * ─────────────────────────────────────────────────────────────────────────────
 * An unreported door is `null` — "this vehicle never told us" — and it renders as
 * such. `false` is the claim that the door is SHUT, and on a car with no CAN adapter
 * that claim is invented. A closed-door icon for a vehicle that reports no doors at
 * all is exactly the plausible-looking wrong answer Phase 4 exists to prevent.
 */

/** True means OPEN, and open is the state worth surfacing. */
export const DOOR_FLAG_CHANNELS = [
  'ssf_door_front_left',
  'ssf_door_front_right',
  'ssf_door_rear_left',
  'ssf_door_rear_right',
  'ssf_door_trunk',
  'ssf_engine_cover_open',
  'ssf_roof_open',
] as const satisfies readonly ChannelKey[]

/** True means FASTENED. The alarming state is therefore `false`, not `true`. */
export const SEATBELT_FLAG_CHANNELS = [
  'csf_seatbelt_driver',
  'csf_seatbelt_front_driver',
  'csf_seatbelt_left_driver',
  'csf_seatbelt_right_driver',
  'csf_seatbelt_centre_driver',
] as const satisfies readonly ChannelKey[]

/** True means ON. Neither good nor bad — a fact about the vehicle. */
export const LIGHT_FLAG_CHANNELS = [
  'csf_parking_lights',
  'csf_dipped_headlights',
  'csf_full_beam_headlights',
  'csf_front_fog_lights',
  'csf_rear_fog_lights',
  'csf_additional_front_lights',
  'csf_additional_rear_lights',
  'csf_light_signal',
  'ssf_hazard_lights',
] as const satisfies readonly ChannelKey[]

export const FLAG_GROUP_KEYS = ['doors', 'seatbelts', 'lights'] as const
export type FlagGroupKey = (typeof FLAG_GROUP_KEYS)[number]

export const FLAG_GROUP_CHANNELS: Readonly<Record<FlagGroupKey, readonly ChannelKey[]>> = {
  doors: DOOR_FLAG_CHANNELS,
  seatbelts: SEATBELT_FLAG_CHANNELS,
  lights: LIGHT_FLAG_CHANNELS,
}

/**
 * What `true` means for the group, which decides both the wording and the tone.
 *
 * A door flag and a seatbelt flag are opposite polarities and merging them into one
 * "active" concept produces a green badge for an unfastened belt.
 */
export const FLAG_GROUP_POLARITY: Readonly<
  Record<FlagGroupKey, 'true_is_alert' | 'false_is_alert' | 'neutral'>
> =
  // prettier-ignore
  {
    doors: 'true_is_alert',
    seatbelts: 'false_is_alert',
    lights: 'neutral',
  }

export interface FlagIndicator {
  channel: ChannelKey
  /** `true` = the flag is set. `null` = never reported, which is NOT `false`. */
  value: boolean | null
}

export interface FlagGroup {
  key: FlagGroupKey
  indicators: FlagIndicator[]
  /** How many of the group's channels this vehicle actually reports. */
  reportedCount: number
  /** True when the vehicle reports none of them — the group is absent, not "all off". */
  absent: boolean
  /** Reported flags in the alerting state: doors open, belts unfastened. 0 for lights. */
  alertCount: number
  /** Reported flags whose value is `true`. Lights on, doors open, belts fastened. */
  activeCount: number
}

/**
 * `1`/`0` out of the live projection, `null` for anything absent.
 *
 * The projection stores numbers because that is what the telemetry table holds; a
 * boolean channel arrives as 1 or 0. Anything that is not a finite number is absence,
 * including the `null` the projection writes for a channel it has never seen.
 */
function flagValue(raw: number | boolean | null | undefined): boolean | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'boolean') return raw
  if (!Number.isFinite(raw)) return null
  return raw !== 0
}

export function decomposeFlagGroup(state: LiveVehicleState | null, key: FlagGroupKey): FlagGroup {
  const polarity = FLAG_GROUP_POLARITY[key]
  const indicators = FLAG_GROUP_CHANNELS[key].map((channel) => ({
    channel,
    value: flagValue(state?.channels?.[channel]),
  }))

  const reported = indicators.filter((indicator) => indicator.value !== null)
  const activeCount = reported.filter((indicator) => indicator.value === true).length
  const alertCount =
    polarity === 'true_is_alert'
      ? activeCount
      : polarity === 'false_is_alert'
        ? reported.filter((indicator) => indicator.value === false).length
        : 0

  return {
    key,
    indicators,
    reportedCount: reported.length,
    absent: reported.length === 0,
    alertCount,
    activeCount,
  }
}

export function decomposeFlagGroups(state: LiveVehicleState | null): FlagGroup[] {
  return FLAG_GROUP_KEYS.map((key) => decomposeFlagGroup(state, key))
}

/** i18n key for one indicator's name. One key per channel, FR and EN both required. */
export function flagLabelKey(channel: ChannelKey): string {
  return `vehicles:flags.${channel}`
}
